import { useEffect, useState } from 'react';
import { api } from './api';
import { initTelegram } from './telegram';
import type { SpaceDto, UserDto } from './types';
import { ErrorBanner, Loader } from './components/common';
import { AddPlant } from './screens/AddPlant';
import { Calendar } from './screens/Calendar';
import { Diagnoses } from './screens/Diagnoses';
import { PlantDetail } from './screens/PlantDetail';
import { Plants } from './screens/Plants';
import { Settings } from './screens/Settings';
import { Today } from './screens/Today';

type Tab = 'today' | 'plants' | 'calendar' | 'diagnoses' | 'settings';

const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'today', icon: '📅', label: 'Сегодня' },
  { key: 'plants', icon: '🌿', label: 'Растения' },
  { key: 'calendar', icon: '🗓', label: 'Календарь' },
  { key: 'diagnoses', icon: '🔍', label: 'Диагноз' },
  { key: 'settings', icon: '⚙️', label: 'Ещё' },
];

export function App() {
  const [user, setUser] = useState<UserDto | null>(null);
  const [spaces, setSpaces] = useState<SpaceDto[]>([]);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('today');
  const [openPlantId, setOpenPlantId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [diagnosePlantId, setDiagnosePlantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    initTelegram();
    api
      .authenticate()
      .then((result) => {
        setUser(result.user);
        setSpaces(result.spaces);
        setSpaceId(result.spaces[0]?.id ?? null);
      })
      .catch((e) =>
        setError(
          e instanceof Error && e.message
            ? `${e.message}. Откройте приложение из Telegram.`
            : 'Не удалось авторизоваться',
        ),
      );
  }, []);

  if (error) {
    return (
      <div className="app">
        <ErrorBanner message={error} />
      </div>
    );
  }

  if (!user || !spaceId) {
    return (
      <div className="app">
        <Loader text="Открываю коллекцию…" />
      </div>
    );
  }

  const space = spaces.find((item) => item.id === spaceId) ?? spaces[0];

  function renderContent() {
    if (adding) {
      return (
        <AddPlant
          spaceId={spaceId!}
          onCancel={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            setRefreshKey((k) => k + 1);
            setTab('plants');
          }}
        />
      );
    }

    if (openPlantId) {
      return (
        <PlantDetail
          plantId={openPlantId}
          onBack={() => setOpenPlantId(null)}
          onDiagnose={(plantId) => {
            setOpenPlantId(null);
            setDiagnosePlantId(plantId);
            setTab('diagnoses');
          }}
        />
      );
    }

    switch (tab) {
      case 'today':
        return <Today key={refreshKey} spaceId={spaceId!} onOpenPlant={setOpenPlantId} />;
      case 'plants':
        return (
          <Plants
            key={refreshKey}
            spaceId={spaceId!}
            onOpenPlant={setOpenPlantId}
            onAdd={() => setAdding(true)}
          />
        );
      case 'calendar':
        return <Calendar key={refreshKey} spaceId={spaceId!} />;
      case 'diagnoses':
        return (
          <Diagnoses
            key={refreshKey}
            spaceId={spaceId!}
            presetPlantId={diagnosePlantId}
            onDone={() => setRefreshKey((k) => k + 1)}
          />
        );
      case 'settings':
        return (
          <Settings
            user={user!}
            space={space}
            spaces={spaces}
            onSelectSpace={(id) => {
              setSpaceId(id);
              setRefreshKey((k) => k + 1);
            }}
          />
        );
    }
  }

  return (
    <>
      <div className="app">{renderContent()}</div>
      <nav className="tabbar">
        {TABS.map((item) => (
          <button
            key={item.key}
            className={tab === item.key && !openPlantId && !adding ? 'active' : ''}
            onClick={() => {
              setOpenPlantId(null);
              setAdding(false);
              setTab(item.key);
            }}
          >
            <span className="icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}
